import React, { useEffect } from 'react';

import _ from 'lodash';
import { Button, Form, Input, InputNumber, Switch, TreeSelect, Select, Radio, Checkbox, Row, Col, Card, Space, Typography } from 'antd';
import { useSelector } from 'react-redux';
import { useDisabledModules } from '../../hooks/useDisabledModules';
import iabCategories from '../../constants/iabCategories.json';

const initialValues = {
  isActive: false,
  defaultTmax: 0,
  maxTmax: 0,
  minTmax: 0,
  defaultBidfloor: 0,
  margin: 15,
  revenueSharePercent: 0,
  limitQps: 0,
  dailySpendLimit: 0,
  totalSpendLimit: 0,
  priceFromNurl: false,
  spendCounter: 'i',
  admUrl: false,
  optimizer: true,
};

export default function UpsertSspForm(props) {
  // eslint-disable-next-line react/prop-types
  const { ssp, onFinish, users } = props;

  const [form] = Form.useForm();

  const userInfo = useSelector((state) => state.login);
  const isAdmin = userInfo?.role === 'admin';

  const hasAdmUrl = !useDisabledModules().includes('admUrl');

  useEffect(() => {
    if (ssp) {
      // eslint-disable-next-line @typescript-eslint/no-shadow,react/prop-types
      const iabCategories = (ssp.catsForHB || '').split(',').filter(_.identity);

      form.setFieldsValue({
        ..._.pick(ssp, [
          'isActive',
          'partnerId',
          'name',
          'defaultTmax',
          'maxTmax',
          'minTmax',
          'defaultBidfloor',
          'margin',
          'revenueSharePercent',
          'limitQps',
          'dailySpendLimit',
          'totalSpendLimit',
          'priceFromNurl',
          'note',
          'spendCounter',
          'admUrl',
          'optimizer',
        ]),
        iabCategories,
      });
    }
  }, [props]);

  return (
    <Form
      name="basic"
      layout="vertical"
      onFinish={onFinish}
      autoComplete="off"
      form={form}
      initialValues={initialValues}
    >
      <Card title="Overview" size="small" style={{ marginBottom: 12 }}>
        <Row gutter={12}>
          <Col xs={24} sm={8}>
            <Form.Item label="Active" valuePropName="checked" name="isActive" style={{ marginBottom: 8 }}>
              <Switch />
            </Form.Item>
          </Col>
          <Col xs={24} sm={8}>
            <Form.Item name="partnerId" label="Company" rules={[{ required: true }]} style={{ marginBottom: 8 }}>
              <Select
                /* eslint-disable-next-line react/prop-types */
                options={users.map((u) => ({ label: `[#${u.id}] ${u.firstName}`, value: u.id }))}
                showSearch
                filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={8}>
            <Form.Item label="Name" name="name" rules={[{ required: true }]} style={{ marginBottom: 8 }}>
              <Input />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={12}>
          <Col xs={24} sm={8}>
            <Form.Item
              name="optimizer"
              label="Optimizer"
              tooltip="Managed setting"
              valuePropName="checked"
              style={{ marginBottom: 0 }}
            >
              <Switch disabled={!isAdmin} />
            </Form.Item>
          </Col>
        </Row>
      </Card>

      <Card title="Auction Timing" size="small" style={{ marginBottom: 12 }}>
        <Row gutter={12}>
          <Col xs={24} sm={8}>
            <Form.Item
              label="Default Tmax"
              name="defaultTmax"
              extra="Used when the SSP request does not include tmax."
              style={{ marginBottom: 8 }}
            >
              <InputNumber style={{ width: '100%' }} />
            </Form.Item>
          </Col>
          <Col xs={24} sm={8}>
            <Form.Item
              label="Max Tmax"
              name="maxTmax"
              extra="Requests above this value are capped. Should be higher than Min Tmax."
              style={{ marginBottom: 8 }}
            >
              <InputNumber style={{ width: '100%' }} />
            </Form.Item>
          </Col>
          <Col xs={24} sm={8}>
            <Form.Item
              label="Min Tmax"
              name="minTmax"
              extra="Requests below this value fail validation."
              style={{ marginBottom: 8 }}
            >
              <InputNumber style={{ width: '100%' }} />
            </Form.Item>
          </Col>
        </Row>
      </Card>

      <Card title="Pricing" size="small" style={{ marginBottom: 12 }}>
        <Row gutter={12}>
          <Col xs={24} sm={8}>
            <Form.Item
              label="Default Bid Floor"
              name="defaultBidfloor"
              extra="Fallback when bidfloor is missing in the request."
              style={{ marginBottom: 8 }}
            >
              <InputNumber style={{ width: '100%' }} />
            </Form.Item>
          </Col>
          <Col xs={24} sm={8}>
            <Form.Item
              label="Margin Percent"
              name="margin"
              help="Example: bidfloor 1.00 + 10% margin = 1.10. Ignored if either side sets revenue share."
              style={{ marginBottom: 8 }}
            >
              <InputNumber style={{ width: '100%' }} />
            </Form.Item>
          </Col>
          <Col xs={24} sm={8}>
            <Form.Item
              label="Revenue Share Percent"
              name="revenueSharePercent"
              help="Example: win price 2.50, bidfloor 2.00, 20% share → SSP gets 0.10. Overrides DSP revenue share and margin."
              style={{ marginBottom: 8 }}
            >
              <InputNumber style={{ width: '100%' }} />
            </Form.Item>
          </Col>
        </Row>

        <Card.Meta description="Priority: SSP revenue share → DSP revenue share → DSP margin. Only margin is used when both revenue share fields are 0." />
      </Card>

      <Card title="Spend & Delivery" size="small" style={{ marginBottom: 12 }}>
        <Row gutter={12}>
          <Col xs={24} sm={8}>
            <Form.Item label="QPS Limit" name="limitQps" style={{ marginBottom: 8 }}>
              <InputNumber style={{ width: '100%' }} />
            </Form.Item>
          </Col>
          <Col xs={24} sm={8}>
            <Form.Item label="Daily Spend Limit" name="dailySpendLimit" style={{ marginBottom: 8 }}>
              <InputNumber style={{ width: '100%' }} />
            </Form.Item>
          </Col>
          <Col xs={24} sm={8}>
            <Form.Item label="Total Spend Limit" name="totalSpendLimit" style={{ marginBottom: 8 }}>
              <InputNumber style={{ width: '100%' }} />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item
          label="SSP Spend Counter"
          name="spendCounter"
          style={{ marginBottom: 0 }}
          extra="Impression, Nurl, and Burl are always tracked. This selection defines which event is used for SSP spend, DSP spend, and profit price calculations."
        >
          <Radio.Group>
            <Space orientation="vertical" size={4}>
              <div>
                <Radio value="i">Impression</Radio>
                <Typography.Text type="secondary" style={{ display: 'block', marginLeft: 24 }}>
                  Track prices by impression events.
                </Typography.Text>
              </div>
              <div>
                <Radio value="n">Nurl</Radio>
                <Typography.Text type="secondary" style={{ display: 'block', marginLeft: 24 }}>
                  Track prices by NURL callback events.
                </Typography.Text>
              </div>
              <div>
                <Radio value="b">Burl</Radio>
                <Typography.Text type="secondary" style={{ display: 'block', marginLeft: 24 }}>
                  Track prices by BURL billing events.
                </Typography.Text>
              </div>
            </Space>
          </Radio.Group>
        </Form.Item>
      </Card>

      <Card title="Content & Delivery" size="small" style={{ marginBottom: 12 }}>
        <Form.Item label="IAB Categories" name="iabCategories">
          <TreeSelect
            treeData={iabCategories}
            treeCheckable
            showCheckedStrategy={TreeSelect.SHOW_PARENT}
            style={{ width: '100%', minWidth: '300px' }}
          />
        </Form.Item>

        {hasAdmUrl && (
          <Form.Item name="admUrl" valuePropName="checked" style={{ marginBottom: 0 }}>
            <Checkbox>Hide ADM content and serve via URL?</Checkbox>
          </Form.Item>
        )}
      </Card>

      <Form.Item label="Notes" name="note">
        <Input.TextArea allowClear />
      </Form.Item>

      <Form.Item>
        <Button type="primary" htmlType="submit" style={{ width: '100%' }}>
          {ssp ? 'Update' : 'Create'} (General)
        </Button>
      </Form.Item>
    </Form>
  );
}
